import { useEffect, useMemo } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import type { i18n as II18n } from 'i18next';

import { Format } from './format/formats';
import { FormattableValue, TFunction, translate } from './translate';
import { useForceUpdate } from './useForceUpdate';

export type i18nType = Omit<II18n, 'format'> & { format: (value: FormattableValue, format?: Format) => string };
export interface TProps {
  t: TFunction<string>;
  i18n: i18nType;
}
export { II18n };

function useEventListener(this: II18n, event: string, callback: (...args: unknown[]) => void) {
  useEffect(() => {
    this.on(event, callback);
    return () => this.off(event, callback);
  }, [event, callback]);
}

export function useTranslation(): TProps {
  const forceUpdate = useForceUpdate();
  const { i18n } = useI18nTranslation();

  // i18n gets mutated, we want a new t when this happens so that the forced update gets the new lang
  // eslint-disable-next-line react-hooks/exhaustive-deps -- forcing update for i18n.language
  const t = useMemo(() => translate<string>((i18nKey, values) => i18n.t(i18nKey, values)), [i18n, i18n.language]);

  useEventListener.call(i18n, 'languageChanged', forceUpdate);

  return { i18n, t };
}
